import { createFileRoute, Link } from "@tanstack/react-router";
import { ToolLayout } from "@/components/layout/ToolLayout";
import { pageHead, breadcrumbJsonLd, organizationJsonLd, ldScript, absoluteUrl } from "@/lib/seo";

const PATH = "/about";
const TITLE = "About ReadBarcode — Barcode, QR & GS1 Toolkit";
const DESC =
  "ReadBarcode.com is an independent browser-based barcode toolkit for reading, generating, validating, printing, and understanding UPC, EAN, GTIN, QR Code, Data Matrix, and GS1 barcode formats.";

const CRUMBS = [
  { name: "Home", path: "/" },
  { name: "About", path: PATH },
];

export const Route = createFileRoute("/about")({
  component: AboutPage,
  head: () => ({
    ...pageHead({
      title: TITLE,
      description: DESC,
      path: PATH,
      ogType: "website",
      ogImage: absoluteUrl("/og/about.jpg"),
      ogImageAlt:
        "About ReadBarcode.com — an independent browser-based barcode toolkit for reading, generating and validating barcodes.",
    }),
    scripts: [ldScript(organizationJsonLd()), ldScript(breadcrumbJsonLd(CRUMBS))],
  }),
});

function AboutPage() {
  return (
    <ToolLayout
      eyebrow="About"
      title="About ReadBarcode.com"
      description="An independent browser-based barcode toolkit built to make barcode reading, generation, validation, printing, and learning easier. Every tool runs in your browser without an account, so you can read, generate, validate and decode barcodes from one site on any device."
      crumbs={CRUMBS}
      content={
        <>
          <p>
            ReadBarcode.com is an independent web platform built and maintained by an independent
            developer/operator. It exists to read, generate, validate, print, export, and understand
            barcodes in a practical, privacy-first way.
          </p>

          <h2>Why ReadBarcode.com Exists</h2>
          <p>
            ReadBarcode.com was built because most barcode tools are either too narrow, too technical,
            split across many websites, or cluttered with unnecessary friction. The goal is to offer
            one clean place to read, generate, validate, print, export, and understand barcodes.
          </p>

          <h2>What You Can Do Here</h2>
          <ul>
            <li>
              <strong>Read barcodes</strong> from your camera or an uploaded image — see the <Link to="/barcode-reader">barcode reader</Link>, <Link to="/qr-code-reader">QR reader</Link>, <Link to="/data-matrix-reader">Data Matrix reader</Link> and <Link to="/read-barcode-from-image">read-from-image</Link>. The same tools install as a <Link to="/read-barcode-app">browser-based app</Link> on phone, tablet or desktop.
            </li>
            <li>
              <strong>Generate barcodes</strong> in 1D and 2D formats — <Link to="/barcode-generator">UPC, EAN, Code 128, ITF-14, Code 39, Codabar</Link>, <Link to="/qr-code-generator">QR</Link>, <Link to="/wifi-qr-code-generator">Wi-Fi QR</Link>, <Link to="/vcard-qr-generator">vCard QR</Link>, <Link to="/data-matrix-generator">Data Matrix</Link>, <Link to="/pdf417-generator">PDF417</Link>, <Link to="/aztec-code-generator">Aztec</Link>, and <Link to="/gs1-digital-link-generator">GS1 Digital Link</Link>.
            </li>
            <li>
              <strong>Validate and look up</strong> a value — verify <Link to="/upc-lookup">UPC</Link>
              , <Link to="/ean-lookup">EAN</Link>, <Link to="/isbn-barcode-reader">ISBN</Link> and <Link to="/gtin-validator">GTIN</Link> check digits, compute one with the <Link to="/check-digit-calculator">check digit calculator</Link>, or identify an unknown
              value with the <Link to="/barcode-type-detector">barcode type detector</Link>.
            </li>
            <li>
              <strong>Print labels and export data</strong> — generators support PNG, SVG and
              Avery-sheet print layouts. <Link to="/batch-barcode-scanner">Batch tools</Link> let you
              clean and export large lists of barcodes to CSV.
            </li>
            <li>
              <strong>Run a lightweight inventory</strong> from your browser with the <Link to="/inventory-scanner">inventory scanner</Link>.
            </li>
            <li>
              <strong>Learn the formats</strong> behind everything else with the <Link to="/barcode-guides">barcode guides</Link>.
            </li>
          </ul>

          <h2>Built for Practical Users</h2>
          <p>
            ReadBarcode.com is useful for ecommerce, retail, inventory, logistics, small businesses,
            developers, researchers, librarians, and everyday users — anyone who deals with barcodes
            as part of getting something done, not as a hobby.
          </p>

          <h2>Privacy-First by Design</h2>
          <p>
            Most barcode reading, image decoding, barcode generation, and export actions happen
            directly in your browser. Camera scans, uploaded images, generated barcodes, and entered
            barcode values are not uploaded to our servers for normal tool use. See the <Link to="/privacy">Privacy Policy</Link> for the full details on what is and isn&apos;t
            collected.
          </p>

          <h2>Built for Today&apos;s Barcodes and Tomorrow&apos;s Retail</h2>
          <p>
            The toolkit covers the 1D barcodes still in use at the point of sale today (UPC, EAN, Code
            128, ITF-14) and the 2D codes the retail industry is moving toward (QR, Data Matrix, GS1
            Digital Link). The <Link to="/barcode-guides/sunrise-2027-explained">Sunrise 2027 guide</Link> and the <Link to="/gs1-digital-link-decoder">Digital Link decoder</Link> are aimed at brands and
            retailers preparing for the 2D-at-POS transition.
          </p>
          <p>
            ReadBarcode.com is independent and is not affiliated with, endorsed by, sponsored by,
            certified by, or operated by GS1.
          </p>

          <h2>Independent and Actively Maintained</h2>
          <p>
            ReadBarcode.com is an independent project focused entirely on barcode tools, barcode
            education, and practical barcode workflows. It is actively maintained, with updates
            focused on barcode accuracy, browser compatibility, format support, performance,
            accessibility, and practical user experience.
          </p>

          <h2>Start With Any Barcode</h2>
          <p>
            Point a camera at one with the <Link to="/barcode-reader">barcode reader</Link>, generate
            a fresh symbol with the <Link to="/barcode-generator">barcode generator</Link>, or paste a
            value into the <Link to="/barcode-type-detector">barcode type detector</Link> to see what
            it is. Bug reports and feature suggestions are always welcome via the <Link to="/contact">contact page</Link>.
          </p>
        </>
      }
    />
  );
}
